import type { KeyboardEvent } from 'react';
import { LuChevronRight } from 'react-icons/lu';
import RecordPanel from '../components/RecordPanel';

/**
 * A lista do que um número do Painel Operacional contou.
 *
 * Pedido dele em 17/10/2026: todo número da tela abre as oportunidades por trás
 * dele — "em Entradas, aparece a lista com o que entrou e informações relevantes,
 * status, etc". Painel lateral extra largo (não modal), para as colunas ficarem
 * "tranquilas". A linha abre a ficha, como no Precisa de ação.
 */

export interface LinhaLista {
    id: number;
    contato: string;
    etapa: string;
    responsavel: string | null;
    /** A data que importa para ESTA lista: entrada, ou fechamento em Ganhos e Perdidos. */
    data: string | null;
    origem: string | null;
    valor: number;
}

export interface ListaAberta {
    titulo: string;
    /** "11/10 – 17/10", "Lucas Almeida · paradas", "Obras Aceleradas." */
    contexto: string;
    /** Rótulo da coluna de data: "Entrou em" ou "Fechou em". */
    rotuloData: string;
    /** Conta que explica o número quando ele não é a própria lista (Conversão). */
    nota?: string;
    linhas: LinhaLista[];
}

const brl = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 });

const dataCurta = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isFinite(d.getTime()) ? d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—';
};

interface Props {
    lista: ListaAberta | null;
    /** Algum filtro da tela está ligado: a lista diz isso, senão "36" sem contexto vira dúvida. */
    comFiltros: boolean;
    celular: boolean;
    onClose: () => void;
    onAbrir: (id: number) => void;
}

const PainelLista = ({ lista, comFiltros, celular, onClose, onAbrir }: Props) => {
    if (!lista) return null;
    const total = lista.linhas.reduce((s, l) => s + (l.valor || 0), 0);
    const n = lista.linhas.length;

    const abrir = (id: number) => () => onAbrir(id);
    const teclado = (id: number) => (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAbrir(id); }
    };

    return (
        <RecordPanel
            open
            onClose={onClose}
            extraWide
            title={lista.titulo}
            subtitle={comFiltros ? `${lista.contexto} · com os filtros da tela` : lista.contexto}
        >
            <div className="op-pl">
                <p className="op-pl-resumo">
                    <b>{n.toLocaleString('pt-BR')} {n === 1 ? 'oportunidade' : 'oportunidades'}</b>
                    {' · '}{brl(total)}
                    {lista.nota && <span className="op-pl-nota"> · {lista.nota}</span>}
                </p>

                {n === 0 ? (
                    <p className="op-empty">Nenhuma oportunidade.</p>
                ) : celular ? (
                    <ul className="op-lista">
                        {lista.linhas.map(l => (
                            <li key={l.id} className="op-lista-item op-row-link" role="link" tabIndex={0}
                                aria-label={`Abrir a oportunidade de ${l.contato}`}
                                onClick={abrir(l.id)} onKeyDown={teclado(l.id)}>
                                <span className="op-lista-top">
                                    <span className="op-cell-name">{l.contato}</span>
                                    <span className="op-pl-etapa">{l.etapa}</span>
                                </span>
                                {/* Três linhas: com tudo numa só, responsável, data e origem cortavam juntos em 390px. */}
                                <span className="op-lista-bot">
                                    <span className={l.responsavel ? 'op-cell-muted' : 'op-cell-faint'}>{l.responsavel || 'sem responsável'}</span>
                                    <span className="op-cell-muted op-pl-fixo">· {lista.rotuloData.toLowerCase()} {dataCurta(l.data)}</span>
                                </span>
                                <span className="op-lista-bot">
                                    <span className="op-cell-muted">{l.origem || '—'}</span>
                                    <b className="op-lista-valor">{brl(l.valor || 0)}</b>
                                </span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <table className="op-table op-pl-table">
                        <colgroup>
                            <col />
                            <col className="op-pl-col-etapa" />
                            <col className="op-pl-col-resp" />
                            <col className="op-pl-col-data" />
                            <col className="op-pl-col-origem" />
                            <col className="op-pl-col-valor" />
                            <col className="op-pl-col-go" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>Contato</th>
                                <th>Etapa</th>
                                <th>Responsável</th>
                                <th>{lista.rotuloData}</th>
                                <th>Origem</th>
                                <th className="op-col-right">Valor</th>
                                <th aria-label="Abrir" />
                            </tr>
                        </thead>
                        <tbody>
                            {lista.linhas.map(l => (
                                <tr key={l.id} className="op-row-link" tabIndex={0} role="link"
                                    aria-label={`Abrir a oportunidade de ${l.contato}`}
                                    onClick={abrir(l.id)} onKeyDown={teclado(l.id)}>
                                    <td className="op-pl-txt op-pl-nome" title={l.contato}>{l.contato}</td>
                                    <td className="op-pl-txt op-cell-muted">{l.etapa}</td>
                                    <td className={`op-pl-txt ${l.responsavel ? 'op-cell-muted' : 'op-cell-faint'}`}>
                                        {l.responsavel || 'sem responsável'}
                                    </td>
                                    <td className="op-cell-muted">{dataCurta(l.data)}</td>
                                    <td className="op-pl-txt op-cell-muted" title={l.origem ?? undefined}>{l.origem || '—'}</td>
                                    <td className="op-col-right">{brl(l.valor || 0)}</td>
                                    <td className="op-col-go"><LuChevronRight size={15} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </RecordPanel>
    );
};

export default PainelLista;
